<template>
  <div>
    <b-overlay
      :show="!dataIsReady"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <failed-data-get
        v-if="FailedDataGetStatus"
        text="Looks like this server doesn't exist." />

      <template v-else>
        <div class="d-flex align-items-end pt-2 pb-6">
          <div class="d-flex flex-column">
            <h1 class="h5 mb-2">
              Cloudflare Proxy configuration of «{{ selectedServer.name }}»
            </h1>
            <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
              <b-breadcrumb-item :to="{ name: 'Dashboard' }">
                <i class="fa fa-home fa-sm mr-2"></i>
                Home
              </b-breadcrumb-item>
              <b-breadcrumb-item :to="{ name: 'ServersList'}">Servers</b-breadcrumb-item>
              <b-breadcrumb-item active>Cloudflare Proxy</b-breadcrumb-item>
            </b-breadcrumb>
          </div>
        </div>

        <cloudflare-status-alert />

        <b-row>
          <b-col xl="9">
            <b-card
              no-body
              class="card-custom card-border mb-6"
              style="min-height: 100px;">
              <b-card-body v-show="dataIsReady">

                <b-table
                  :items="cloudflareRules"
                  :fields="fields"
                  :class="{ 'table-hover' : !cloudflareRulesEmpty }"
                  stacked="lg"
                  head-variant=""
                  show-empty
                  responsive
                  class="mb-6">

                  <!-- custom cells -->
                  <template #cell(order)="data">
                    <span class="small font-weight-light">{{ data.index + 1 }}</span>
                  </template>
                  <template #cell(code)="data">
                    <b-form-group class="mb-0">
                      <b-form-input
                        :id="`input-${data.item._id}-code`"
                        size="sm"
                        placeholder="Code"
                        :state="getValidationState(data.item._id, 'code')"
                        @input="clearManageCloudflareError(data.item._id, 'code')"
                        v-model="data.item.code"
                        class="mb-0 rounded" />
                      <b-form-invalid-feedback
                        v-for="(error, index) in manageCloudflareError(data.item._id, 'code')"
                        :key="`${error.field}-${index}`">
                        {{ error.message }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </template>
                  <template #cell(proxy)="data">
                    <b-form-select
                      :id="`input-${data.item._id}-proxy`"
                      size="sm"
                      :value="data.item.proxy"
                      :options="availableProxies"
                      value-field="value"
                      text-field="name"
                      class="mb-0">
                    </b-form-select>
                  </template>
                  <template #cell(type)="data">
                    <b-form-input
                      :id="`input-${data.item._id}-type`"
                      size="sm"
                      placeholder="Type"
                      disabled
                      v-model="data.item.type"
                      class="mb-0 rounded" />
                  </template>
                  <template #cell(port)="data">
                    <b-form-group class="mb-0">
                      <b-form-input
                        :id="`input-${data.item._id}-port`"
                        type="number"
                        min="1"
                        max="65535"
                        size="sm"
                        placeholder="8080"
                        :state="getValidationState(data.item._id, 'port')"
                        @input="clearManageCloudflareError(data.item._id, 'port')"
                        @wheel="$event.target.blur()"
                        v-model.number="data.item.port"
                        class="mb-0 rounded" />
                      <b-form-invalid-feedback
                        v-for="(error, index) in manageCloudflareError(data.item._id, 'port')"
                        :key="`${error.field}-${index}`">
                        {{ error.message }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </template>
                  <template #cell(value)="data">
                    <b-form-group class="mb-0">
                      <b-form-input
                        :id="`input-${data.item._id}-value`"
                        size="sm"
                        placeholder="https://dns.nextdns.io/c88dk3"
                        :state="getValidationState(data.item._id, 'value')"
                        @input="clearManageCloudflareError(data.item._id, 'value')"
                        v-model="data.item.value"
                        class="mb-0 rounded" />
                      <b-form-invalid-feedback
                        v-for="(error, index) in manageCloudflareError(data.item._id, 'value')"
                        :key="`${error.field}-${index}`">
                        {{ error.message }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </template>
                  <template #cell(edit)="data">
                    <b-button
                      :id="`deleteButton-${data.item._id}`"
                      size="sm"
                      variant="link-primary"
                      @click="removecloudflareField(data.item._id)">
                      <i class="fa fa-trash fa-sm"></i>
                      <span>Delete</span>
                    </b-button>
                  </template>

                  <!-- empty state -->
                  <template #empty="scope">
                    <no-data-found text="No proxy rules on this server" />
                    <b-button
                      v-if="savedCloudflareRulesLength == 0"
                      variant="transparent-primary"
                      size="sm"
                      class="mx-auto d-block"
                      @click="addCloudflareField">
                      <i class="fa fa-plus fa-sm" />
                      <span>Add New Rule</span>
                    </b-button>
                  </template>
                </b-table>

                <!-- Proxy rules toolbar -->
                <div
                  v-if="savedCloudflareRulesLength > 0 || newCloudflareRulesLength > 0"
                  class="d-flex align-items-center">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="addCloudflareField">
                    <i class="fa fa-plus fa-sm" />
                    <span>Add New Rule</span>
                  </b-button>
                  <div class="d-flex align-items-center ml-auto">
                    <span
                      v-if="cloudflareChanged"
                      v-b-tooltip.hover.top
                      title="Changes are not saved"
                      class="svg-icon svg-icon-md mr-3">
                      <inline-svg
                        class="svg-icon m-0"
                        src="media/svg/icons/Code/Warning-2-circle.svg"/>
                    </span>
                    <b-button
                      variant="primary"
                      size="sm"
                      v-wave
                      :disabled="!cloudflareChanged"
                      :class="{ 'no-events' : !cloudflareChanged }"
                      @click="saveCloudflareRules">
                      <span>Save Settings</span>
                      <i class="fa fa-sm fa-save ml-2" />
                    </b-button>
                  </div>
                </div>

              </b-card-body>
            </b-card>
          </b-col>
          <b-col xl="3">
            <server-details-plate custom-item="cloudflate_installed" />
          </b-col>
        </b-row>

      </template>
    </b-overlay>

    <modal-remove-cloudflare />
  </div>
</template>

<script>
import {
  // actions
  GET_SERVER,
  GET_CLOUDFLARE,
  SAVE_CLOUDFLARE_RULES,

  // mutations
  SELECT_SERVER,
  UNSELECT_SERVER,
  SET_FAILED_SERVER_GET,
  SET_CLOUDFLARE_PENDING_STATUS,
  CLEAR_CLOUDFLARE,
  ADD_CLOUDFLARE_FIELD,
  REMOVE_CLOUDFLARE_FIELD,
  SET_CLOUDFLARE_ERROR,
  CLEAR_CLOUDFLARE_ERROR,
  CLEAR_CLOUDFLARE_ERRORS,
  CLEAR_CLOUDFLARE_SNAPSHOT,
} from '@/core/services/store/servers.module'
import { GET_COUNTRIES } from '@/core/services/store/countries.module'
import { GLOBAL_WORDS } from '@/core/services/store/config.module.js'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import { showToast } from '@/core/utils'
import { regex_code, regex_url } from '@/core/regex'
import ModalRemoveCloudflare from './components/ModalRemoveCloudflare.vue'
import CloudflareStatusAlert from './components/CloudflareStatusAlert.vue'
import ServerDetailsPlate from './components/ServerDetailsPlate.vue'

const ACTIVE_INTERVAL   = 10000   // 10 sec
const UNACTIVE_INTERVAL = 300000  // 5 min

export default {
  name: 'ServersCloudflare',
  components: {
    ModalRemoveCloudflare,
    CloudflareStatusAlert,
    ServerDetailsPlate,
  },
  data() {
    return {
      fields: [
        {
          label: '#',
          key: 'order',
          class: ['pl-5', 'align-middle'],
          thStyle: 'width: 50px;'
        },
        {
          label: 'Code',
          key: 'code',
          class: [],
          thStyle: 'width: 20%;'
        },
        {
          label: 'Proxy',
          key: 'proxy',
          class: ['text-nowrap'],
          thStyle: 'width: 140px;'
        },
        {
          label: 'Type',
          key: 'type',
          class: ['text-nowrap'],
          thStyle: 'width: 8%;'
        },
        {
          label: 'Port',
          key: 'port',
          class: [],
          thStyle: 'width: 10%;'
        },
        {
          label: 'Upstream',
          key: 'value',
          class: [],
          thStyle: 'width: 27%;'
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['text-md-right', 'text-nowrap', 'align-middle', 'pr-5 pl-8 pl-lg-20'],
        }
      ],

      dataIsReady: false,
      checkInterval: ACTIVE_INTERVAL,
      checkTimer: null,

      showToast,
    }
  },
  computed: {
    ...mapGetters([
      'selectedServer',
      'selectedServerId',
      'FailedDataGetStatus',
      'cloudflareData',
      'cloudflareInstalled',
      'cloudflarePending',
      'cloudflareRules',
      'cloudflareRule',
      'cloudflareChanged',
      'cloudflareRulesEmpty',
      'newCloudflareRulesLength',
      'savedCloudflareRulesLength',
      'availableProxies',
      'manageCloudflareError',
      'manageCloudflareErrors',
      'manageCloudflareErrorsLength',
      'manageCloudflareErrorsEmpty',
      'serversResponses',
    ]),

    routeId() {
      return this.$route.params.id
    }
  },
  watch: {
    /* eslint-disable-next-line no-unused-vars */
    routeId(newVal, oldVal) {
      if (newVal) {
        this.dataIsReady = false
        this.$store.commit(UNSELECT_SERVER)
        this.getServer(newVal)
      }
    },

    /* eslint-disable-next-line no-unused-vars */
    cloudflarePending(newVal, oldVal) {
      if (newVal) {
        this.setCheckInterval()
      } else {
        this.clearCheckTimer()
      }
    },

    cloudflareInstalled(newVal, oldVal) {
      if (oldVal !== undefined && newVal !== oldVal) {
        this.clearCheckTimer()
      }
    },
  },
  created() {
    this.$store.dispatch(GET_COUNTRIES)
    this.$store.commit(SET_FAILED_SERVER_GET, false)

    this.getServer(this.routeId)

    window.addEventListener('beforeunload', this.handlePageUnload)
  },
  mounted() {
    const that = this

    document.addEventListener('visibilitychange', () => {
      that.clearCheckTimer()
      that.checkInterval = document.hidden ? UNACTIVE_INTERVAL : ACTIVE_INTERVAL
      that.setCheckInterval()
    })

    eventBus.$on('showToast/ServersCloudflare', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  beforeRouteLeave(to, from, next) {
    if (this.cloudflareChanged) {
      const userConfirmed = window.confirm(GLOBAL_WORDS['unsaved_data'])
      userConfirmed ? next() : next(false)
    } else {
      next()
    }
  },
  beforeDestroy() {
    this.clearCheckTimer()
    this.dataIsReady = false
    document.removeEventListener('visibilitychange')
    window.removeEventListener('beforeunload', this.handlePageUnload)
  },
  destroyed() {
    eventBus.$off('showToast/ServersCloudflare')

    this.$store.commit(UNSELECT_SERVER)
    this.$store.commit(SET_FAILED_SERVER_GET, false)
    this.$store.commit(SET_CLOUDFLARE_PENDING_STATUS, false)
    this.$store.commit(CLEAR_CLOUDFLARE)
    this.$store.commit(CLEAR_CLOUDFLARE_ERRORS)
    this.$store.commit(CLEAR_CLOUDFLARE_SNAPSHOT)
  },
  methods: {
    getServer(id) {
      this.$store
        .dispatch(GET_SERVER, { id })
        .then(data => {
          this.$store.commit(SELECT_SERVER, data)
        })
        .finally(() => { this.getCloudlfare(id) })
    },

    getCloudlfare(id, save_rules = true) {
      this.$store
        .dispatch(GET_CLOUDFLARE, { server_id: id, save_rules })
        .finally(() => { this.dataIsReady = true })
    },

    validateForm() {
      return new Promise((resolve, reject) => {
        // Check if required fields aren't empty
        const requiredFields = ['code', 'port', 'value']
        this.cloudflareRules.forEach(rule => {
          requiredFields.forEach(field => {
            if (!rule[field]) {
              this.$store.commit(SET_CLOUDFLARE_ERROR, {
                id: rule._id,
                field: field,
                code: `field_${field}_empty`,
                message: 'This field is required'
              })
            }
          })
        })

        // Check if 'port' is a whole number between 1 and 65535
        const invalidPort = this.cloudflareRules.find(rule => rule.port && (!Number.isInteger(rule.port) || rule.port < 1 || rule.port > 65535))
        if (invalidPort) {
          this.$store.commit(SET_CLOUDFLARE_ERROR, {
            id: invalidPort._id,
            field: 'port',
            code: 'invalid_port',
            message: 'Port must be a whole number between 1 and 65535'
          })
        }

        // Check if 'code' is made up of English letters, numbers, and without space, allowing '-' and '_'
        const invalidCode = this.cloudflareRules.find(rule => rule.code && !regex_code.test(rule.code))
        if (invalidCode) {
          this.$store.commit(SET_CLOUDFLARE_ERROR, {
            id: invalidCode._id,
            field: 'code',
            code: 'invalid_code',
            message: 'Code should only contain English letters, numbers, and specific symbols («-» or «_»)'
          })
        }

        // Check if 'value' is a valid URL (http or https)
        const invalidURL = this.cloudflareRules.find(rule => rule.value && !regex_url.test(rule.value))
        if (invalidURL) {
          this.$store.commit(SET_CLOUDFLARE_ERROR, {
            id: invalidURL._id,
            field: 'value',
            code: 'invalid_url',
            message: 'Must be a valid URL address'
          })
        }

        // Check if there are any duplicates in 'code' or 'port' fields
        function findDuplicates(array, field) {
          const codeMap = {}
          for (const obj of array) {
            const code = obj[field]
            if (!codeMap[code]) {
              codeMap[code] = [obj]
            } else {
              codeMap[code].push(obj)
            }
          }
          const duplicates = Object.values(codeMap).filter(objects => objects.length > 1)
          return duplicates.flat()
        }

        function handleDuplicates(duplicates, field, code, message) {
          duplicates.forEach(dub => {
            if (dub[field]) {
              this.$store.commit(SET_CLOUDFLARE_ERROR, {
                id: dub._id,
                field,
                code,
                message
              })
            }
          })
        }

        handleDuplicates.call(
          this,
          findDuplicates(this.cloudflareRules, 'code'),
          'code',
          'code_duplicated',
          'Codes should be unique'
        )
        handleDuplicates.call(
          this,
          findDuplicates(this.cloudflareRules, 'port'),
          'port',
          'port_duplicated',
          'Ports should be unique'
        )

        // Return validation result
        if (this.manageCloudflareErrorsEmpty) {
          resolve(true)
        } else {
          reject(false)
        }
      })
    },

    saveCloudflareRules() {
      this.clearManageCloudflareErrors()
      this.validateForm()
        .then(() => {
          this.$store.dispatch(SAVE_CLOUDFLARE_RULES, {
            action: 'update',
            server_id: this.selectedServerId,
          }).then(() => {
            this.getCloudlfare(this.routeId)
          })
        })
        .catch(() => {
          this.showToast('Incorrect form, check again', 'danger')
        })
    },

    setCheckInterval() {
      this.checkTimer = setInterval(() => {
        if (this.cloudflarePending) {
          this.getCloudlfare(this.routeId, false)
        }
      }, this.checkInterval)
    },

    clearCheckTimer() {
      clearInterval(this.checkTimer)
    },

    addCloudflareField() {
      this.$store.commit(ADD_CLOUDFLARE_FIELD)
    },

    removecloudflareField(id) {
      this.$store.commit(REMOVE_CLOUDFLARE_FIELD, id)
    },

    getValidationState(id, field) {
      return this.manageCloudflareError(id, field).length > 0 ? false : null
    },

    clearManageCloudflareError(id, field) {
      this.$store.commit(CLEAR_CLOUDFLARE_ERROR, { id, field })
    },

    clearManageCloudflareErrors() {
      this.$store.commit(CLEAR_CLOUDFLARE_ERRORS)
    },

    handlePageUnload(event) {
      if (this.cloudflareChanged) {
        const message = GLOBAL_WORDS['unsaved_data']
        event.returnValue = message
        return message
      }
    },
  },
}
</script>
